<template>
  <div v-clickoutside="handleClose" :class="modifiers">
    <div class="dropdown__trigger" @click="toggleDropdown">
      <slot />
    </div>

    <!-- DESKTOP -->
    <div v-show="isPopperShown" :class="'dropdown__popper--' + popperMinWidthSize" class="dropdown__popper">
      <slot name="content" />
    </div>

    <!-- MOBILE -->
    <action-sheet
      v-if="!isDisabled && $screen.isMobile($mq)"
      :title="title"
      :visible="isOpen"
      class="dropdown__action-sheet"
      @close="handleClose"
    >
      <template #list>
        <slot name="content" />
      </template>
    </action-sheet>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent, markRaw, PropType } from 'vue'
import { createPopper, type Instance as PopperInstance, type Placement } from '@popperjs/core'
import { ClickOutside as Clickoutside } from 'element-plus'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */

/* API */

export default defineComponent({
  name: 'Dropdown',

  directives: { Clickoutside },

  props: {
    /* Popper placements. See https://popper.js.org/docs/v2/constructors/#placement */
    placement: {
      type: String,
      default: 'bottom-start',
    },
    /* [skidding, distance] in px: moves the popper along the trigger and away from it. See
       https://popper.js.org/docs/v2/modifiers/offset/. Popper sets the popper's margin to 0, so use this, not a margin */
    offset: {
      type: Array as unknown as PropType<[number, number]>,
      default: undefined,
    },
    title: {
      type: String,
      default: 'Options',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    forceVisibility: {
      type: Boolean,
      default: false,
    },
    /* small = 100px, medium = 200px, large = 300px, extra-large = 360px, ginormous = 480px */
    popperMinWidthSize: {
      type: String,
      default: 'medium',
    },
  },

  emits: ['close', 'open'],

  data() {
    return {
      isOpen: false,
      popper: null as PopperInstance | null,
    }
  },

  computed: {
    /* On mobile the action sheet shows the content instead */
    isPopperShown(): boolean {
      return this.isOpen && !this.$screen.isMobile(this.$mq) && !this.isDisabled
    },

    modifiers(): Array<string> {
      const baseSelector = 'dropdown'
      const selectors = [baseSelector]

      if (this.isDisabled) {
        selectors.push(baseSelector + '--disabled')
      }

      return selectors
    },
  },

  watch: {
    /**
     * This will be used if the parent wants to force the close or open of the dropdown
     */
    forceVisibility: {
      immediate: true,
      handler(value: boolean) {
        this.isOpen = value
      },
    },
    placement(value: string) {
      this.popper?.setOptions({ placement: value as Placement })
    },
  },

  mounted() {
    this.positionPopper()
  },

  updated() {
    this.positionPopper()
  },

  beforeUnmount() {
    this.popper?.destroy()
  },

  methods: {
    /**
     * Places the open popper by the trigger, and keeps it there as the content changes or the page scrolls
     */
    positionPopper() {
      if (!this.isPopperShown) {
        this.popper?.destroy()
        this.popper = null
      } else if (this.popper) {
        this.popper.update()
      } else {
        const reference = this.$el.querySelector('.dropdown__trigger')
        const popper = this.$el.querySelector('.dropdown__popper')
        this.popper = markRaw(
          createPopper(reference, popper, {
            placement: this.placement as Placement,
            modifiers: [
              // The offset prop, read on every update. Without it, a 4px gap below the trigger and none above it, as
              // the popper's margin-top gave with Popper 1 (Popper 2 sets the margin to 0)
              {
                name: 'offset',
                options: {
                  offset: ({ placement }: { placement: Placement }) =>
                    this.offset ?? [0, placement.startsWith('bottom') ? 4 : 0],
                },
              },
              // As with Popper 1: keep 5px inside the window or scrolling container, flip to the trigger's other side
              // (above it for bottom placements, keeping the start/end alignment) when there's no room, and cover the
              // trigger when there's room on neither side
              { name: 'preventOverflow', options: { padding: 5, altAxis: true, tether: false } },
              { name: 'flip', options: { padding: 5, flipVariations: false } },
            ],
          }),
        )
      }
    },

    toggleDropdown() {
      if (!this.isDisabled) {
        this.isOpen = !this.isOpen
        if (this.isOpen) {
          this.$emit('open')
        } else {
          this.$emit('close')
        }
      }
    },

    handleClose() {
      if (this.isOpen) {
        this.$emit('close')
        this.isOpen = false
      }
    },
  },
})
</script>

<style lang="scss">
.dropdown {
  display: inline-block;
  max-width: 100%;

  &__trigger {
    cursor: pointer;
  }

  &__popper {
    position: absolute;
    z-index: 1050;
    width: auto;
    max-width: 400px;
    max-height: 440px;
    height: auto;
    padding: 8px;
    overflow: auto;
    background-color: var(--color-general-white);
    border-radius: 8px;
    box-shadow: $--select-dropdown-shadow;

    h5:not(:first-of-type) {
      // Buffer for multiple H5 headings
      padding-top: 10px;
    }

    &--small {
      min-width: 100px;
    }

    &--medium {
      min-width: 200px;
    }

    &--large {
      min-width: 300px;
    }

    &--extra-large {
      min-width: 360px;
    }

    /* Standard popper renderings for ul, li */
    > div {
      /* If multiple contents, space them out...*/
      &:not(:first-child) {
        margin-top: 12px;
      }

      /* Many dropdowns will have a sleek-filter-input search nested within primary div */
      > .sleek-filter-input {
        margin-bottom: 10px;
        min-width: 200px;
        width: 100%;
      }

      > h5 {
        margin: 4px 4px 10px 4px;
      }

      > hr {
        border: none;
        border-top: solid 1px #ccc;
      }

      > ul {
        position: relative;
        padding: 0;
        margin: 0;

        > li {
          display: flex;
          align-items: center;
          padding: $dropdown-item-li-vertical-padding
            $dropdown-item-li-horizontal-padding;
          margin: 0;
          list-style: none;
          cursor: pointer;
          border-radius: 6px;
          @include lettering--p-sm;

          &:hover,
          &:active {
            background: var(--color-background-hover);
          }

          /* Optional icon */
          > :first-child.ddi:not(.ddi-check),
          > a :first-child.ddi:not(.ddi-check),
          > span :first-child.ddi:not(.ddi-check) {
            top: -1px;
            margin-right: 8px;
            color: var(--color-text-third);

            &.ddi.ddi-star-filled {
              color: $color--orange-40;
            }
          }

          /* If using avatar (customer, assignee, member pickers for example) */
          :first-child.avatar {
            margin-right: 6px;
          }

          .ddi.ddi-trash {
            color: $color--red;
          }

          > span {
            @include single-line-ellipsis;
          }

          /* Selected item */
          > span.selected {
            display: flex;
            align-items: center;
            width: 100%;
            font-weight: $bold;
            color: $color--blue;

            &.muted {
              color: $color--gray-45;
            }

            &::after {
              margin-left: auto;
              padding-left: 10px;
              font-family: icons;
              content: '\f108';
            }
          }

          /* For use if item data is a link and should be right-clickable */
          a {
            text-decoration: none;
            color: inherit;
          }
        }
      }
      /* Used typically for empty state */
      > span {
        display: flex;
        align-items: center;
        justify-content: left;
        padding: 8px 10px;
        font-size: 14px;
        color: var(--color-text-third);
      }
    }
  }

  &__action-sheet {
    .action-sheet-body {
      margin-bottom: 32px;

      /* Standard action sheet renderings for ul, li */
      > div {
        /* If multiple contents, space them out...*/
        &:not(:first-child) {
          margin-top: 12px;
        }

        /* Many dropdowns will have a sleek-filter-input search nested within primary div */
        > .sleek-filter-input {
          margin-bottom: 10px;
        }

        > h5 {
          padding: 0;
          margin: 4px 4px 10px 4px;
        }

        > hr {
          border: none;
          border-top: solid 1px #ccc;
        }

        > ul {
          position: relative;
          padding: 0;
          margin: 0;

          > li {
            display: flex;
            align-items: center;
            padding: $dropdown-item-li-vertical-padding
              $dropdown-item-li-horizontal-padding;
            margin: 0;
            list-style: none;
            cursor: pointer;
            border-radius: 6px;
            @include lettering--p;

            &:hover,
            &:active {
              background: var(--color-background-hover);
            }

            /* Optional icon */
            > :first-child.ddi:not(.ddi-check),
            > a :first-child.ddi:not(.ddi-check),
            > span :first-child.ddi:not(.ddi-check) {
              top: -1px;
              margin-right: 8px;
              color: var(--color-text-third);

              &.ddi.ddi-star-filled {
                color: $color--orange-40;
              }
            }

            /* If using avatar (customer, assignee, member pickers for example) */
            :first-child.avatar {
              margin-right: 6px;
            }

            .ddi.ddi-trash {
              color: $color--red;
            }

            > span {
              @include single-line-ellipsis;
            }

            /* Selected item */
            > span.selected {
              display: flex;
              align-items: center;
              width: 100%;
              font-weight: $bold;
              color: $color--blue;

              &.muted {
                color: $color--gray-45;
              }

              &::after {
                margin-left: auto;
                padding-left: 10px;
                font-family: icons;
                content: '\f108';
              }
            }

            /* For use if item data is a link and should be right-clickable */
            a {
              text-decoration: none;
              color: inherit;
            }
          }
        }

        /* Used typically for empty state */
        > span {
          display: flex;
          align-items: center;
          justify-content: left;
          padding: 8px 10px;
          font-size: 14px;
          color: var(--color-text-third);
        }
      }
    }
  }
}
</style>
